import { useNavigate } from 'react-router-dom';
import {
  Divider,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import {
  CalendarClock20Regular,
  Grid20Regular,
  SignOut20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import { UserAvatar } from '@/components/common/UserChip';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth/auth-provider';
import { useTeams } from '@/lib/teams/teams-context';
import { useTheme } from '@/hooks/useTheme';
import { documentThemeToTeamsTheme, type DocumentThemeName } from '@/lib/teams/theme';

/**
 * Account menu in the header.
 *
 * Inside Teams we never offer "cerrar sesión" (the host owns the session) nor a
 * theme switch (the host owns the theme); both appear only in the browser.
 */

export interface UserMenuProps {
  name: string;
  email?: string | null;
  avatarUrl?: string | null;
  isAdmin: boolean;
  className?: string;
}

export function UserMenu({
  name,
  email,
  avatarUrl,
  isAdmin,
  className,
}: UserMenuProps): JSX.Element {
  const navigate = useNavigate();
  const { isInTeams } = useTeams();
  const { signOut } = useAuth();
  const { documentTheme, canChangeTheme, setTheme } = useTheme();

  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          title={email ? `${name} · ${email}` : name}
          aria-label={`Cuenta de ${name}`}
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-pill transition-colors duration-fast ease-standard',
            'hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
            className,
          )}
        >
          <UserAvatar name={name} avatarUrl={avatarUrl} size={28} />
        </button>
      </MenuTrigger>

      <MenuPopover>
        <div className="flex items-center gap-3 px-3 py-2.5">
          <UserAvatar name={name} avatarUrl={avatarUrl} size={40} />
          <div className="min-w-0">
            <p className="truncate text-body font-semibold text-ink">{name}</p>
            {email ? <p className="truncate text-caption text-ink-muted">{email}</p> : null}
            {isAdmin ? (
              <p className="mt-1 inline-flex rounded-pill bg-brand-50 px-1.5 py-0.5 text-caption font-semibold text-ink-brand dark:bg-brand-900/60">
                Administrador
              </p>
            ) : null}
          </div>
        </div>

        <Divider />

        <MenuList
          checkedValues={{ theme: [documentTheme] }}
          onCheckedValueChange={(_event, data) => {
            const [next] = data.checkedItems;
            if (next) setTheme(documentThemeToTeamsTheme(next as DocumentThemeName));
          }}
        >
          <MenuItem icon={<CalendarClock20Regular />} onClick={() => navigate('/my-reservations')}>
            Mis reservaciones
          </MenuItem>

          {isAdmin ? (
            <MenuItem icon={<Grid20Regular />} onClick={() => navigate('/admin')}>
              Panel de administración
            </MenuItem>
          ) : null}

          {canChangeTheme ? <MenuDivider /> : null}
          {canChangeTheme ? (
            <MenuItemRadio name="theme" value="light" icon={<WeatherSunny20Regular />}>
              Tema claro
            </MenuItemRadio>
          ) : null}
          {canChangeTheme ? (
            <MenuItemRadio name="theme" value="dark" icon={<WeatherMoon20Regular />}>
              Tema oscuro
            </MenuItemRadio>
          ) : null}

          {!isInTeams ? <MenuDivider /> : null}
          {!isInTeams ? (
            <MenuItem icon={<SignOut20Regular />} onClick={() => void signOut()}>
              Cerrar sesión
            </MenuItem>
          ) : null}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
